import { useState, useCallback } from 'react'
import api from '@/lib/axios'
import { useTranslation } from '@/hooks/useTranslation'

interface ContactFormState {
  name: string
  email: string
  company: string
  service: string
  message: string
}

interface ContactFormErrors {
  [key: string]: string
}

export function useContactVM(flash: { notice: string | null; alert: string | null }) {
  const { t, localePrefix } = useTranslation()

  const [form, setForm] = useState<ContactFormState>({
    name: '',
    email: '',
    company: '',
    service: '',
    message: '',
  })
  const [errors, setErrors] = useState<ContactFormErrors>({})
  const [loading, setLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(
    flash.notice ?? null
  )

  const validate = useCallback((): ContactFormErrors => {
    const errs: ContactFormErrors = {}
    if (!form.name.trim()) errs.name = t('contact.error_name')
    if (!form.email.trim()) errs.email = t('contact.error_email')
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = t('contact.error_email_invalid')
    if (!form.message.trim()) errs.message = t('contact.error_message')
    return errs
  }, [form, t])

  const setField = useCallback(
    (field: keyof ContactFormState) => (value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }))
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
      if (successMessage) setSuccessMessage(null)
    },
    [successMessage]
  )

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const validationErrors = validate()

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors)
        return
      }

      setLoading(true)
      setErrors({})

      try {
        const contactPath = localePrefix ? `${localePrefix}/contact` : '/contact'
        await api.post(contactPath, {
          name: form.name,
          email: form.email,
          company: form.company,
          service: form.service,
          message: form.message,
        })
        setSuccessMessage(t('contact.success'))
        setForm({ name: '', email: '', company: '', service: '', message: '' })
      } catch {
        setErrors({ general: t('contact.error_general') })
      } finally {
        setLoading(false)
      }
    },
    [form, validate, t, localePrefix]
  )

  return {
    name: form.name,
    email: form.email,
    company: form.company,
    service: form.service,
    message: form.message,
    onNameChange: setField('name'),
    onEmailChange: setField('email'),
    onCompanyChange: setField('company'),
    onServiceChange: setField('service'),
    onMessageChange: setField('message'),
    onSubmit: handleSubmit,
    errors,
    loading,
    successMessage,
  }
}
